import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc, type RouterOutputs } from '@org/clients/school';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Skeleton } from '@org/ui';
import { diffCorrection, type CorrectionSegment } from '@org/utils-client';

type PracticeItem = RouterOutputs['conversationPractice']['getHistory'][number];

/** A sentence with only its changed words marked. */
function Marked({ segments, kind }: { segments: CorrectionSegment[]; kind: 'wrong' | 'right' }) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.changed ? (
          <mark
            key={index}
            className={
              kind === 'wrong'
                ? 'rounded bg-rose-100 px-0.5 text-[#cf2e2e] underline decoration-wavy decoration-[#cf2e2e]'
                : 'rounded bg-green-100 px-0.5 font-semibold text-green-800'
            }
          >
            {segment.text}
          </mark>
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
    </>
  );
}

/**
 * The student's free practice conversations with MellinAI, with the AI summary
 * of each one (her decision: teachers see the summaries).
 */
export function MellinAiPracticeCard({ studentId }: { studentId: string }) {
  const { t, i18n } = useTranslation('admin');
  const isPt = i18n.language.startsWith('pt');
  const { data, isLoading } = trpc.conversationPractice.getHistory.useQuery({ studentId, limit: 10 });
  const [openId, setOpenId] = useState<string | null>(null);

  const titleOf = (item: PracticeItem) =>
    item.topic ? (isPt ? item.topic.titlePt : item.topic.titleEn) : t('teacher.studentProfile.mellinai.untitled');

  return (
    <Card className="border border-rose-50 bg-white shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg text-gray-900">{t('teacher.studentProfile.mellinai.title')}</CardTitle>
        <CardDescription>{t('teacher.studentProfile.mellinai.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading && <Skeleton className="h-16 w-full" />}
        {!isLoading && !data?.length && (
          <p className="text-sm text-gray-500">{t('teacher.studentProfile.mellinai.empty')}</p>
        )}
        {data?.map((item) => {
          const open = openId === item.sessionId;
          const corrections = item.evaluation?.corrections ?? [];
          const good = !item.evaluation?.lowContent && (item.score ?? 0) >= (item.maxScore ?? 100) * 0.7;
          return (
            <div key={item.sessionId} className="rounded-md border border-rose-100 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-gray-900">{titleOf(item)}</div>
                  <div className="text-xs text-gray-500">
                    {new Date(item.startedAt).toLocaleDateString(i18n.language, {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {item.plannedDurationSec
                      ? ` · ${t('teacher.studentProfile.mellinai.minutes', { minutes: Math.floor(item.plannedDurationSec / 60) })}`
                      : ''}
                  </div>
                </div>
                {item.score !== null && item.score !== undefined && (
                  <Badge variant="outline" className={good ? 'border-green-200 text-green-700' : 'border-rose-200 text-[#cf2e2e]'}>
                    {item.score}/{item.maxScore ?? 100}
                  </Badge>
                )}
                {item.evaluation?.lowContent && (
                  <Badge variant="secondary">{t('teacher.studentProfile.mellinai.lowContent')}</Badge>
                )}
                <Badge variant="secondary">
                  {t('teacher.studentProfile.mellinai.corrections', { count: corrections.length })}
                </Badge>
                {item.evaluation && (
                  <Button variant="ghost" size="sm" className="text-xs" onClick={() => setOpenId(open ? null : item.sessionId)}>
                    {open ? t('teacher.studentProfile.mellinai.showLess') : t('teacher.studentProfile.mellinai.showMore')}
                  </Button>
                )}
              </div>
              {open && item.evaluation && (
                <div className="mt-3 space-y-3 border-t border-rose-100 pt-3">
                  <p className="text-sm text-gray-700">{item.evaluation.overallFeedback}</p>
                  {corrections.length > 0 && (
                    <ul className="space-y-2">
                      {corrections.map((correction, index) => {
                        const diff = diffCorrection(correction.studentQuote, correction.correctedForm);
                        return (
                        <li key={index} className="rounded bg-rose-50/60 px-3 py-2 text-sm">
                          <div className="text-gray-700">
                            <span className="mr-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                              {t('teacher.studentProfile.mellinai.youSaid')}
                            </span>
                            <Marked segments={diff.wrong} kind="wrong" />
                          </div>
                          <div className="text-gray-900">
                            <span className="mr-2 text-xs font-semibold uppercase tracking-wide text-green-700">
                              {t('teacher.studentProfile.mellinai.say')}
                            </span>
                            <Marked segments={diff.right} kind="right" />
                          </div>
                          {correction.explanation && <p className="mt-1 text-xs text-gray-500">{correction.explanation}</p>}
                        </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
